/**
 * Pilgrim tabs — behaviour for the Tabs (PILGRIM) block.
 *
 * Owner: PILGRIM. Pairs with blocks/pilgrim-tabs.liquid / blocks/_pilgrim-tab.liquid.
 *
 * The markup is fully server-rendered (tablist + panels, first tab active); this
 * element only switches tabs. WAI-ARIA tabs pattern with automatic activation:
 * click or arrow-key focus activates, roving tabindex on the buttons.
 *
 * Contracts with the rest of the theme:
 *  - `pilgrim:tab-empty` (bubbling, from any element inside a panel) hides that
 *    tab entirely — button and panel — e.g. Recently viewed (PILGRIM) with no
 *    browsing history. Emitters also set a `data-pilgrim-empty` attribute,
 *    scanned on connect, so the contract survives either module loading first.
 *    Skipped in the theme editor so merchants can still see and edit the tab.
 *    If no visible tabs remain the whole block hides.
 *  - In the theme editor, selecting a tab block (or anything inside its panel)
 *    reveals that panel via `shopify:block:select`.
 *  - Visibility (PILGRIM) classes may hide a tab's button per viewport; on
 *    connect and on resize the active tab is validated and, if its button is
 *    not visible, the first visible tab takes over.
 */
class PilgrimTabs extends HTMLElement {
  /** @type {number | undefined} */
  #resizeTimer;

  connectedCallback() {
    this.addEventListener('pointerdown', this.#onPointerDown, true);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('pilgrim:tab-empty', this.#onTabEmpty);
    window.addEventListener('resize', this.#onResize);

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.#onEditorSelect);
    }

    // Content that reported empty before this element connected (module load
    // order is nondeterministic) left the attribute instead of a caught event.
    for (const emptied of this.querySelectorAll('[data-pilgrim-empty]')) {
      emptied.dispatchEvent(new CustomEvent('pilgrim:tab-empty', { bubbles: true }));
    }

    this.#ensureVisibleActiveTab();
    this.#relaxPanelTabindex();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
    document.removeEventListener('shopify:block:select', this.#onEditorSelect);
  }

  /** @returns {HTMLButtonElement[]} every tab button of THIS instance (not nested ones) */
  get #tabs() {
    return [...this.querySelectorAll('[role="tab"]')].filter(
      (tab) => tab instanceof HTMLButtonElement && tab.closest('pilgrim-tabs') === this
    );
  }

  get #visibleTabs() {
    return this.#tabs.filter((tab) => !tab.hidden && tab.offsetParent !== null);
  }

  /**
   * WAI-ARIA: a tabpanel is only in the tab sequence (tabindex="0") when it
   * holds nothing focusable itself. The server renders every panel focusable;
   * this drops the attribute from panels that contain a link, button or field.
   * Without it Safari focuses the panel when a product card's plus is clicked
   * inside it (buttons don't take focus there), and the quick-add drawer then
   * restores focus to the panel on close — a focus ring around the whole tab
   * content. Re-run before a pointer interaction too, since lazily loaded
   * content (tag search, recently viewed) arrives after connect.
   */
  #relaxPanelTabindex() {
    for (const panel of this.querySelectorAll('[role="tabpanel"]')) {
      if (panel.closest('pilgrim-tabs') !== this) continue;
      const focusable = panel.querySelector(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) panel.removeAttribute('tabindex');
      else if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '0');
    }
  }

  #onPointerDown = () => {
    this.#relaxPanelTabindex();
  };

  /** @param {HTMLElement} tab */
  #panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
  }

  /**
   * @param {HTMLElement} tab
   * @param {{ focus?: boolean }} [options]
   */
  #activate(tab, { focus = false } = {}) {
    for (const other of this.#tabs) {
      const selected = other === tab;
      other.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (selected) {
        other.removeAttribute('tabindex');
      } else {
        other.setAttribute('tabindex', '-1');
      }
      const panel = this.#panelFor(other);
      if (panel) panel.hidden = !selected;
    }

    tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (focus) tab.focus();
    this.#relaxPanelTabindex();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[role="tab"]');
    if (!(tab instanceof HTMLButtonElement) || tab.closest('pilgrim-tabs') !== this) return;
    this.#activate(tab);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[role="tab"]');
    if (!(tab instanceof HTMLButtonElement) || tab.closest('pilgrim-tabs') !== this) return;

    const tabs = this.#visibleTabs;
    const index = tabs.indexOf(tab);
    if (index === -1) return;

    let next;
    switch (event.key) {
      case 'ArrowRight':
        next = tabs[(index + 1) % tabs.length];
        break;
      case 'ArrowLeft':
        next = tabs[(index - 1 + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    if (next) this.#activate(next, { focus: true });
  };

  /** @param {Event} event */
  #onTabEmpty = (event) => {
    if (window.Shopify && window.Shopify.designMode) return;
    if (!(event.target instanceof Element)) return;

    const panel = event.target.closest('[role="tabpanel"]');
    if (!panel || panel.closest('pilgrim-tabs') !== this) return;

    const tab = this.#tabs.find((candidate) => this.#panelFor(candidate) === panel);
    if (!tab) return;

    const wasSelected = tab.getAttribute('aria-selected') === 'true';
    tab.hidden = true;
    panel.hidden = true;

    const remaining = this.#visibleTabs;
    if (remaining.length === 0) {
      this.hidden = true;
    } else if (wasSelected) {
      this.#activate(remaining[0]);
    }
  };

  /** @param {Event} event */
  #onEditorSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    const panel = event.target.closest('[role="tabpanel"]');
    if (!panel || panel.closest('pilgrim-tabs') !== this) return;

    const tab = this.#tabs.find((candidate) => this.#panelFor(candidate) === panel);
    if (tab && tab.getAttribute('aria-selected') !== 'true') this.#activate(tab);
  };

  #onResize = () => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => this.#ensureVisibleActiveTab(), 150);
  };

  /** Viewport-dependent visibility classes can hide the active tab's button. */
  #ensureVisibleActiveTab() {
    const active = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (active && !active.hidden && active.offsetParent !== null) return;

    const first = this.#visibleTabs[0];
    if (first) this.#activate(first);
  }
}

if (!customElements.get('pilgrim-tabs')) {
  customElements.define('pilgrim-tabs', PilgrimTabs);
}
